import React, { useState } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import toast from "react-hot-toast";
import {
  sanitizeFormInput,
  sanitizeUserProfile,
} from "../../utils/sanitization";
import { withCSRFProtection } from "../../utils/csrfProtection";
import { withRateLimit, checkRateLimit } from "../../utils/rateLimiting";
import { validatePasswordStrength } from "../../lib/passwordUtils";

interface RegistrationFormProps {
  onSwitchToLogin: () => void;
  onRegistrationSuccess: () => void;
}

interface FormData {
  name: string;
  asal_sekolah: string;
  role: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSwitchToLogin,
  onRegistrationSuccess,
}) => {
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    asal_sekolah: "",
    role: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validatePassword = (password: string) => {
    const errors = [];
    if (password.length < 8) errors.push("minimal 8 karakter");
    if (!/[A-Z]/.test(password)) errors.push("huruf besar");
    if (!/[a-z]/.test(password)) errors.push("huruf kecil");
    if (!/[0-9]/.test(password)) errors.push("angka");
    return errors;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Sanitize and validate name
    const sanitizedName = sanitizeUserProfile(formData.name);
    if (!sanitizedName.trim()) {
      newErrors.name = "Nama harus diisi";
    } else if (sanitizedName !== formData.name) {
      newErrors.name = "Nama mengandung karakter yang tidak diizinkan";
    }

    // Sanitize and validate school
    const sanitizedSchool = sanitizeUserProfile(formData.asal_sekolah);
    if (!sanitizedSchool.trim()) {
      newErrors.asal_sekolah = "Asal sekolah harus diisi";
    } else if (sanitizedSchool !== formData.asal_sekolah) {
      newErrors.asal_sekolah =
        "Asal sekolah mengandung karakter yang tidak diizinkan";
    }

    // Sanitize and validate role
    const sanitizedRole = sanitizeUserProfile(formData.role);
    if (!sanitizedRole.trim()) {
      newErrors.role = "Role harus diisi";
    } else if (sanitizedRole !== formData.role) {
      newErrors.role = "Role mengandung karakter yang tidak diizinkan";
    }

    // Validate email
    const sanitizedEmail = sanitizeFormInput(formData.email);
    if (!sanitizedEmail) {
      newErrors.email = "Email harus diisi";
    } else if (!/\S+@\S+\.\S+/.test(sanitizedEmail)) {
      newErrors.email = "Format email tidak valid";
    } else if (sanitizedEmail !== formData.email) {
      newErrors.email = "Email mengandung karakter yang tidak diizinkan";
    }

    // Validate password using the imported function
    const passwordErrors = validatePasswordStrength(formData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = `Password harus mengandung ${passwordErrors.join(", ")}`;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Konfirmasi password tidak cocok";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check rate limiting
    const rateLimitCheck = checkRateLimit("AUTH_ATTEMPTS");
    if (!rateLimitCheck.allowed) {
      toast.error(
        rateLimitCheck.message ||
          "Terlalu banyak percobaan. Silakan tunggu sebentar."
      );
      return;
    }

    setLoading(true);

    try {
      // Apply CSRF protection and rate limiting
      const result = (await withCSRFProtection(
        {
          email: sanitizeFormInput(formData.email),
          password: formData.password, // Don't sanitize password
          name: sanitizeUserProfile(formData.name),
          asal_sekolah: sanitizeUserProfile(formData.asal_sekolah),
          role: sanitizeUserProfile(formData.role),
        },
        async sanitizedData => {
          return await withRateLimit("AUTH_ATTEMPTS", async () => {
            return await signUp(sanitizedData.email, sanitizedData.password, {
              name: sanitizedData.name,
              asal_sekolah: sanitizedData.asal_sekolah,
              role: sanitizedData.role,
            });
          });
        }
      )) as { success: boolean; error?: string };

      if (result.success) {
        // Show success toast
        toast.success("Akun berhasil dibuat!");

        // Wait for 1 second to let user see the toast
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Stop loading state
        setLoading(false);

        // Trigger the registration success dialog
        onRegistrationSuccess();
      } else {
        setLoading(false);
        if (result.error) {
          setErrors({ submit: result.error });
        }
      }
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Terjadi kesalahan saat mendaftar";
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    // For user profile fields (name, school, role), allow free typing including spaces
    // Sanitization will be applied during validation, not during typing
    let sanitizedValue = value;

    // Only sanitize email during typing (to prevent obvious issues)
    if (field === "email") {
      sanitizedValue = sanitizeFormInput(value);
    }
    // Don't sanitize name, asal_sekolah, role during typing - allow spaces
    // Don't sanitize password fields as it might interfere with special characters

    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const getPasswordStrength = () => {
    const errors = validatePassword(formData.password);
    if (formData.password.length === 0) return { strength: 0, text: "" };
    if (errors.length === 0) return { strength: 100, text: "Kuat" };
    if (errors.length <= 2) return { strength: 60, text: "Sedang" };
    return { strength: 30, text: "Lemah" };
  };

  // Check if form is valid for button state
  const isFormValid =
    formData.name.trim() !== "" &&
    formData.asal_sekolah.trim() !== "" &&
    formData.role.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.password.trim() !== "" &&
    formData.confirmPassword.trim() !== "";

  const passwordStrength = getPasswordStrength();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name Field */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Nama
        </label>
        <Input
          id="name"
          type="text"
          value={formData.name}
          onChange={e => handleInputChange("name", e.target.value)}
          className={`h-12 rounded-xl border-2 ${
            errors.name ? "border-red-500" : "border-[#e3e6eb]"
          } focus:border-[#6b2cfe] transition-colors`}
          placeholder="Nama Guru"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      {/* School Field */}
      <div>
        <label
          htmlFor="asal_sekolah"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Asal Sekolah
        </label>
        <Input
          id="asal_sekolah"
          type="text"
          value={formData.asal_sekolah}
          onChange={e => handleInputChange("asal_sekolah", e.target.value)}
          className={`h-12 rounded-xl border-2 ${
            errors.asal_sekolah ? "border-red-500" : "border-[#e3e6eb]"
          } focus:border-[#6b2cfe] transition-colors`}
          placeholder="SMPN 1 Yogyakarta"
        />
        {errors.asal_sekolah && (
          <p className="mt-1 text-sm text-red-500">{errors.asal_sekolah}</p>
        )}
      </div>

      {/* Role Field */}
      <div>
        <label
          htmlFor="role"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Role
        </label>
        <Input
          id="role"
          type="text"
          value={formData.role}
          onChange={e => handleInputChange("role", e.target.value)}
          className={`h-12 rounded-xl border-2 ${
            errors.role ? "border-red-500" : "border-[#e3e6eb]"
          } focus:border-[#6b2cfe] transition-colors`}
          placeholder="Matematika"
        />
        {errors.role && (
          <p className="mt-1 text-sm text-red-500">{errors.role}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Email
        </label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={e => handleInputChange("email", e.target.value)}
          className={`h-12 rounded-xl border-2 ${
            errors.email ? "border-red-500" : "border-[#e3e6eb]"
          } focus:border-[#6b2cfe] transition-colors`}
          placeholder="Tulis email"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email}</p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Password
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={e => handleInputChange("password", e.target.value)}
            className={`h-12 rounded-xl border-2 pr-12 ${
              errors.password ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="••••••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <EyeOffIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData.password && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    passwordStrength.strength >= 60
                      ? "bg-green-500"
                      : passwordStrength.strength >= 30
                        ? "bg-yellow-500"
                        : "bg-red-500"
                  }`}
                  style={{ width: `${passwordStrength.strength}%` }}
                ></div>
              </div>
              <span
                className={`text-xs font-medium ${
                  passwordStrength.strength >= 60
                    ? "text-green-600"
                    : passwordStrength.strength >= 30
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                {passwordStrength.text}
              </span>
            </div>
          </div>
        )}

        {errors.password && (
          <p className="mt-1 text-sm text-red-500">{errors.password}</p>
        )}
      </div>

      {/* Confirm Password Field */}
      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium text-[#373e46] mb-1"
        >
          Konfirmasi Password
        </label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={e => handleInputChange("confirmPassword", e.target.value)}
            className={`h-12 rounded-xl border-2 pr-12 ${
              errors.confirmPassword ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="••••••••••••"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showConfirmPassword ? (
              <EyeOffIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
        )}
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading || !isFormValid}
        className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 mt-6 ${
          isFormValid && !loading
            ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Mendaftar...
          </div>
        ) : (
          "Daftar"
        )}
      </Button>

      {/* Switch to Login */}
      <div className="text-center pt-4">
        <p className="text-sm text-[#373e46] mb-3">Sudah memiliki akun?</p>
        <Button
          type="button"
          onClick={onSwitchToLogin}
          className="w-full h-12 bg-[#e4e6ff] text-[#6b2cfe] font-medium rounded-xl hover:bg-[#d1d5ff] transition-all duration-300"
        >
          Masuk Sekarang
        </Button>
      </div>
    </form>
  );
};
